import { useState, useRef } from "react";
import { Icons } from "assets";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { LoginVS } from "shared/utils/validation";
import { LoginService } from "shared/services/auth";
import { useDispatch } from "react-redux";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { initSocket } from "shared/services/socket";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import classNames from "classnames";
import CustomCheckbox from "shared/components/common/customCheckbox";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import styles from "./styles.module.scss";

const initialValues: LoginType = {
  email: "",
  password: "",
  remember_me: false,
};

const ERROR_TOAST_DEBOUNCE_MS = 6000;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const lastErrorToastAt = useRef<number>(0);

  const formik = useFormik({
    initialValues,
    validationSchema: LoginVS,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      setSubmitting(true);
      try {
        const response = await LoginService({
          email: values.email,
          password: values.password,
          remember_me: values.remember_me,
        });

        const data = response.data;
        const token = data?.tokens?.accessToken;
        const refreshToken = data?.tokens?.refreshToken;
        const user = data?.user;
        const onboarding = data?.onboarding;
        const onboardingProjects = Array.isArray(data?.onboarding_projects)
          ? data.onboarding_projects
          : [];

        if (token) {
          sessionStorage.removeItem("onboarding_dismissed");
          dispatch(
            setAuthReducer({
              isLoggedIn: true,
              token,
              refreshToken,
              user: user,
              onboarding,
              onboarding_projects: onboardingProjects,
              onboarding_project: onboardingProjects[0] || null,
            }),
          );

          const numericUserId = user?.user_id || user?.id;
          if (numericUserId) {
            initSocket(typeof numericUserId === 'string' ? parseInt(numericUserId, 10) : numericUserId);
          }

          const userType = user?.user_type;

          // Enter the onboarding flow whenever the client has any project
          // awaiting onboarding. The flow processes them one at a time,
          // advancing to the next after each successful submission.
          const isNavigatingToOnboarding =
            userType === "client" && onboardingProjects.length > 0;

          if (!isNavigatingToOnboarding) {
            toastMessage("success", "Login successful!");
          }

          setTimeout(() => {
            if (isNavigatingToOnboarding) {
              navigate(routeConstant.onboarding.path, {
                replace: true,
                state: { projectId: onboardingProjects[0].project_id },
              });
            } else {
              const defaultRoutes: { [key: string]: string } = {
                client: routeConstant.client.projects.path,
                designer: routeConstant.designer.home.path,
                builder: routeConstant.builder.projects.path,
                collaborator: routeConstant.collaborator.projects.path,
              };

              navigate(
                defaultRoutes[userType] || routeConstant.designer.home.path,
                { replace: true },
              );
            }
          }, 0);
        } else {
          console.error("Token not found in response:", data);
          toastMessage(
            "error",
            "Invalid response from server - token not found",
          );
        }
      } catch (error: any) {
        const now = Date.now();
        if (now - lastErrorToastAt.current > ERROR_TOAST_DEBOUNCE_MS) {
          lastErrorToastAt.current = now;
          handleErrors(error);
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const { handleChange, handleSubmit, values, touched, errors } = formik;

  return (
    <section
      className={classNames(
        "w-full flex flex-col items-center relative",
        styles.leftContainer,
      )}
    >
      <div className="flex items-center w-full justify-center m-auto">
        <form
          className={styles.formContainer}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          noValidate
        >
          <div className="flex flex-col gap-3 text-center">
            <h1
              className={classNames(styles.title, "text-center items-center")}
            >
              Welcome! <span>👋🏼</span>
            </h1>

            <p className={classNames(styles.subTitle, "text-center")}>
              Please login to access exclusive features and make the most of
              your stay.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <CustomInput
              label="Email Address"
              placeholder="e.g. abc@example.com"
              required
              Icon={Icons.Mail}
              type="email"
              value={values.email}
              error={touched.email && errors.email ? errors.email : ""}
              onChange={handleChange("email")}
              customLabelStyle={styles.labelStyle}
            />

            <CustomInput
              label="Password"
              placeholder="•••••••••••"
              required
              isPassword
              Icon={Icons.Lock}
              type="password"
              value={values.password}
              error={touched.password && errors.password ? errors.password : ""}
              onChange={handleChange("password")}
              customLabelStyle={styles.labelStyle}
            />

            <div className="flex items-center justify-between">
              <CustomCheckbox
                label="stay logged in"
                labelClass={styles.loginCheckbox}
                checked={values.remember_me}
                onChange={() =>
                  formik.setFieldValue("remember_me", !values.remember_me)
                }
                filtersCheckbox={styles.customCheckbox}
              />

              <Link
                to={routeConstant.forgotPassword.path}
                className={styles.link}
              >
                <span> Forgot password?</span>
              </Link>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 ">
            <CustomButton
              type="submit"
              title="Log In"
              style={{ width: "100%" }}
              titleStyle={classNames(styles.buttonTitle)}
              Icon={Icons.RightArrow}
              IconDirection="left"
              className={styles.btn}
              loading={loading}
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </section>
  );
};

export default Login;
